import { useCallback } from 'react';
import {
  get,
  post,
  put,
  patch,
  destroy,
} from './index';

/**
   * Send request to get all users
   * @param {number} orgId organization id
   * @returns Promise with http response
   */

const getCsrfToken = () => {
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
};

export const getUsers = (orgId, limit, currentPage) => {
  return get(`/api/v1/users?organization_id=${orgId}}&limit=${limit}&offset=${limit * (currentPage - 1)}`);
};

/**
 * Send request to create user
 * @param {number} orgId
 * @param {object} formData
 * @returns Promise with http response
 * we need send second argument as empty object to match the function signature
 */

export const createUser = (orgId, _, formData) => {
  const formattedData = {
    user: { ...formData },
  };
  return post(`/api/v1/users?organization_id=${orgId}`, formattedData);
};
/**
 * Send request to update user
 * @param {number} orgId
 * @param {number} userId
 * @param {object} formData
 * @returns Promise with http response
 */
export const updateUser = (orgId, userId, formData) => {
  const { isUpdatedByManager = false, ...rest } = formData;
  const formattedData = {
    user: {
      ...rest,
      is_updated_by_manager: isUpdatedByManager,
    },
    organization_id: orgId,
  };
  return put(`/api/v3/users/${userId}`, formattedData);
};

/**
 * Custom hook to fetch users
 * @param {number} orgId
 * @param {number} limit
 * @param {number} currentPage
 * @param {function} showToast
 * @param {function} setUsers
 * @param {function} setTotalItems
 * @returns {function} that fetches users
 * useCallback is used to prevent too many renders
 */
export const useFetchUsers = (orgId, limit, currentPage, showToast, setUsers, setTotalItems) => useCallback(() => {
  getUsers(orgId, limit, currentPage)
    .then((response) => response.json())
    .then((data) => {
      setUsers(data.users);
      setTotalItems(data.meta.totalCount);
    })
    .catch((error) => {
      showToast({ message: error.message, type: 'error' });
    });
}, [orgId, limit, currentPage, showToast, setUsers, setTotalItems]);

export const updateUserInfo = (userData, apiVersion = 'v1') => {
  const csrfToken = getCsrfToken();
  return patch(
    // Default v1 keeps legacy flow; pass 'v3' for redesign email-change flow.
    `/api/${apiVersion}/registrations/edit`, userData,
    {
      headers: { 'X-CSRF-Token': csrfToken },
    },
  );
};

export const deactivateUser = (userId, orgId) => {
  return destroy(`/api/v1/users/${userId}/organizations/${orgId}`);
};

export const resendConfirmation = (userId, orgId) => {
  return post('/api/v1/users/resend_confirmation', {
    user_id: userId,
    organization_id: orgId,
  });
};
